import { useState } from 'react'

import { useToasts } from '@/hooks/useToasts'
import { useUpload } from '@/hooks/useUpload'

import { useCreateUserFeedbackMutation } from '@/graphql/generated/hooks'
import { UploadedFileType } from '@/graphql/generated/schemas'

import { CreateFeedbackFormInputsIProps } from '../components/CreateUserFeedbackForm'
import { MAX_FILENAME_LENGTH, MAX_FILE_SIZE } from '../constants/constants'

interface UseCreateFeedbackIProps {
  onClose: () => void
}

interface UseCreateFeedbackReturnIProps {
  onCreateUserFeedback: (
    values: CreateFeedbackFormInputsIProps
  ) => Promise<void>
  isLoading: boolean
}

export const useCreateUserFeedback = ({
  onClose,
}: UseCreateFeedbackIProps): UseCreateFeedbackReturnIProps => {
  const [isLoading, setIsLoading] = useState(false)
  const { showSuccess, showError } = useToasts()
  const [upload] = useUpload()
  const [createUserFeedback] = useCreateUserFeedbackMutation()

  const onCreateUserFeedback = async (
    values: CreateFeedbackFormInputsIProps
  ): Promise<void> => {
    setIsLoading(true)

    try {
      let storedAssetId: string | undefined
      if (values.file && values.file.length > 0) {
        const file = values.file[0]

        try {
          storedAssetId = await upload({
            file,
            fileType: UploadedFileType.UserFeedbackMedia,
            validationOptions: {
              maxFileSizeInBytes: MAX_FILE_SIZE,
              maxFileNameLength: MAX_FILENAME_LENGTH,
            },
          })
        } catch (error) {
          showError(error.message || 'Failed to upload file. Please try again.')
          setIsLoading(false)
          return
        }
      }

      // Step 2: Create feedback with or without media
      await createUserFeedback({
        variables: {
          input: {
            message: values.message,
            storedAssetId,
          },
        },
      })

      showSuccess('Thank you for your feedback!')
      setIsLoading(false)
      onClose()
    } catch (error) {
      showError('Failed to submit feedback. Please try again.')
      setIsLoading(false)
    }
  }

  return {
    onCreateUserFeedback: onCreateUserFeedback,
    isLoading,
  }
}
